/**
 * Betriebskosten (BKA) — Übersicht
 * =================================
 * Zeigt alle Abrechnungsperioden pro Objekt mit Status, Belegquote und
 * Positions-Anzahl. Neu-Anlage per Dialog: Objekt + Zeitraum + Modus
 * (normal / SEG-Import aus fremder WEG-Abrechnung).
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatEUR, formatDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Receipt, ArrowRight, Warning, LockKey, CheckCircle, Buildings, Trash, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import SearchableCombobox from "@/components/SearchableCombobox";
import BulkCreateDialog from "@/components/bka/BulkCreateDialog";
import { sortByPropertyNumber, comparePropertyNumber, addressText } from "@/components/bka/bkaUtils";
import { MagnifyingGlass } from "@phosphor-icons/react";

const STATUS_META = {
  draft:     { label: "Entwurf",       cls: "bg-slate-100 text-slate-700 border-slate-200" },
  locked:    { label: "Abgeschlossen", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  sent:      { label: "Versendet",     cls: "bg-blue-100 text-blue-800 border-blue-200" },
  booked:    { label: "Verbucht",      cls: "bg-purple-100 text-purple-800 border-purple-200" },
  cancelled: { label: "Storniert",     cls: "bg-rose-100 text-rose-800 border-rose-200" },
};

export default function Betriebskosten() {
  const [rows, setRows] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  // 2026-02 (iter122) — BKA-Assistent
  const [suggestions, setSuggestions] = useState([]);
  const [creatingSuggestions, setCreatingSuggestions] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [selectedSuggestions, setSelectedSuggestions] = useState({});
  const [search, setSearch] = useState("");
  const [yearFilter, setYearFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: periods }, { data: props }, { data: sug }] = await Promise.all([
        api.get("/bka/periods"),
        api.get("/properties"),
        api.get("/bka/assistant/suggestions").catch(() => ({ data: { suggestions: [] } })),
      ]);
      setRows(periods);
      setProperties(props);
      setSuggestions(sug?.suggestions || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const handleDelete = async (period) => {
    const label = period.label || `${period.period_from?.slice(0, 10)}`;
    if (!await window._domivusConfirm(
      `Abrechnung "${label}" wirklich löschen?\n\n` +
      `Alle zugehörigen Positionen, Belege, Zuordnungen und Einzelabrechnungen ` +
      `werden mit gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.`
    )) return;
    try {
      await api.delete(`/bka/periods/${period.id}`);
      toast.success("Abrechnung gelöscht");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const handleCreateSuggestions = async () => {
    const chosen = suggestions.filter((s) => selectedSuggestions[`${s.property_id}-${s.year}`]);
    if (chosen.length === 0) {
      toast.info("Bitte mindestens einen Vorschlag auswählen.");
      return;
    }
    setCreatingSuggestions(true);
    try {
      const { data } = await api.post("/bka/assistant/create-suggested", chosen);
      toast.success(`${data.created_count} Abrechnung(en) angelegt${data.skipped ? ` (${data.skipped} übersprungen)` : ""}`);
      setSelectedSuggestions({});
      setAssistantOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Anlegen fehlgeschlagen");
    } finally { setCreatingSuggestions(false); }
  };

  const years = useMemo(() => [...new Set(rows.map((r) => (r.period_from || "").slice(0, 4)).filter(Boolean))].sort().reverse(), [rows]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (yearFilter !== "all" && !(r.period_from || "").startsWith(yearFilter)) return false;
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (!q) return true;
      return [r.property_name, r.property_number, r.property_address, r.label].filter(Boolean).join(" ").toLowerCase().includes(q);
    });
  }, [rows, search, yearFilter, statusFilter]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((r) => {
      const key = r.property_id;
      if (!g[key]) g[key] = { property: r, items: [] };
      g[key].items.push(r);
    });
    return Object.values(g).sort((a, b) => comparePropertyNumber(a.property.property_number, b.property.property_number)
      || (a.property.property_name || "").localeCompare(b.property.property_name || ""));
  }, [filtered]);

  return (
    <div className="space-y-5" data-testid="bka-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            <Receipt size={24} className="text-emerald-700" />
            Betriebskostenabrechnung
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Rechtssichere BKA nach § 556 BGB, § 556a BGB und BetrKV § 2. Für Mietshäuser,
            Einfamilienhäuser, WEG-verwaltete Objekte und SEG-Einheiten in fremd-verwalteten WEGs.
          </p>
        </div>
        <div className="flex items-center gap-2">
        <BulkCreateDialog properties={properties} periods={rows} onCreated={load} />
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="bka-new-btn" className="gap-1.5">
              <Plus size={16} /> Neue Abrechnung
            </Button>
          </DialogTrigger>
          <NewPeriodDialog properties={properties} onCreated={() => { load(); setDialogOpen(false); }} />
        </Dialog>
        </div>
      </div>

      {rows.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="bka-filter-bar">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-8" placeholder="Abrechnung suchen: Objekt, Objekt-ID, Adresse, Bezeichnung…"
                   value={search} onChange={(e) => setSearch(e.target.value)} data-testid="bka-search" />
          </div>
          <Select value={yearFilter} onValueChange={setYearFilter}>
            <SelectTrigger className="w-40" data-testid="bka-year-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Jahre</SelectItem>
              {years.map((y) => <SelectItem key={y} value={y}>Wirtschaftsjahr {y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="bka-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Status</SelectItem>
              {Object.entries(STATUS_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-xs text-slate-500" data-testid="bka-filter-count">{filtered.length} von {rows.length} Abrechnungen</span>
        </div>
      )}

      {/* 2026-02 (iter122) — BKA-Assistent: schlägt fehlende Wirtschaftsjahre
          für aktiv verwaltete Objekte vor (basierend auf Verwaltervertrag). */}
      {suggestions.length > 0 && (
        <div className="rounded-lg border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-white p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                <Sparkle size={18} className="text-amber-700" weight="fill" />
              </div>
              <div>
                <div className="text-sm font-semibold text-slate-900">
                  {suggestions.length} Wirtschaftsjahr(e) noch nicht abgerechnet
                </div>
                <div className="text-xs text-slate-600 mt-0.5 max-w-xl">
                  Basierend auf Ihren Verwalterverträgen sollten für folgende Objekte
                  BKA-Perioden angelegt werden. Klicken Sie „Details ansehen" um zu prüfen.
                </div>
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={() => setAssistantOpen(true)} data-testid="bka-assistant-open">
              Details ansehen
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500 py-10 text-center">Lade…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-slate-200 py-14 text-center bg-slate-50/50">
          <Receipt size={40} className="mx-auto text-slate-300 mb-3" />
          <div className="text-slate-500 mb-3">Noch keine Abrechnungen angelegt.</div>
          <div className="text-xs text-slate-400 max-w-lg mx-auto">
            Starte mit „Neue Abrechnung" — das System führt dich durch Positionen erfassen,
            Belege verknüpfen und die Erstellung der Mieter-Einzelabrechnungen.
          </div>
        </div>
      ) : grouped.length === 0 ? (
        <div className="text-sm text-slate-500 py-10 text-center" data-testid="bka-filter-empty">Keine Abrechnungen für die gewählten Filter.</div>
      ) : (
        <div className="space-y-4">
          {grouped.map((g) => (
            <div key={g.property.property_id} className="rounded-lg border border-slate-200 bg-white overflow-hidden">
              <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex items-center gap-2">
                <Buildings size={16} className="text-slate-500" />
                <span className="font-medium text-sm text-slate-900">
                  {g.property.property_number && <span className="font-mono text-slate-500 mr-2 text-xs font-normal">{g.property.property_number}</span>}
                  {g.property.property_name || "—"}
                  {g.property.property_address && <span className="text-slate-400 ml-2 text-xs font-normal">{g.property.property_address}</span>}
                </span>
              </div>
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Bezeichnung</th>
                    <th className="text-left px-4 py-2 font-medium w-40">Zeitraum</th>
                    <th className="text-left px-4 py-2 font-medium w-24">Modus</th>
                    <th className="text-right px-4 py-2 font-medium w-24">Positionen</th>
                    <th className="text-left px-4 py-2 font-medium w-36">Status</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {g.items.map((r) => {
                    const s = STATUS_META[r.status] || STATUS_META.draft;
                    return (
                      <tr key={r.id} data-testid={`bka-row-${r.id}`} className="hover:bg-slate-50">
                        <td className="px-4 py-2.5">
                          <Link to={`/betriebskosten/${r.id}`} className="text-slate-900 font-medium hover:underline">
                            {r.label || "Wirtschaftsjahr"}
                          </Link>
                        </td>
                        <td className="px-4 py-2.5 text-xs text-slate-600 tabular-nums">
                          {formatDate(r.period_from)} – {formatDate(r.period_to)}
                        </td>
                        <td className="px-4 py-2.5">
                          {r.mode === "seg_import" ? (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-800 border border-violet-200">SEG-Import</span>
                          ) : (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">Objekt</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-right text-xs tabular-nums text-slate-600">
                          {r.positions_count ?? 0}
                        </td>
                        <td className="px-4 py-2.5">
                          <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${s.cls}`}>
                            {r.status === "locked" && <LockKey size={10} />}
                            {r.status === "sent" && <CheckCircle size={10} />}
                            {s.label}
                          </span>
                        </td>
                        <td className="px-2 py-2.5 text-right">
                          <div className="inline-flex items-center gap-1">
                            <Link to={`/betriebskosten/${r.id}`} data-testid={`bka-open-${r.id}`}>
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Öffnen">
                                <ArrowRight size={14} />
                              </Button>
                            </Link>
                            {r.status === "draft" && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                onClick={() => handleDelete(r)}
                                data-testid={`bka-delete-${r.id}`}
                                title="Abrechnung löschen (nur Entwürfe)"
                              >
                                <Trash size={14} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {/* 2026-02 (iter122) — BKA-Assistent Dialog */}
      <Dialog open={assistantOpen} onOpenChange={setAssistantOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkle size={18} className="text-amber-600" weight="fill" />
              BKA-Assistent
            </DialogTitle>
            <DialogDescription>
              Wählen Sie die Wirtschaftsjahre, für die eine BKA angelegt werden soll.
              Vorschläge basieren auf Verwalterbeginn + fehlenden Perioden.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500 bg-slate-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left w-8">
                    <input
                      type="checkbox"
                      checked={suggestions.length > 0 && suggestions.every((s) => selectedSuggestions[`${s.property_id}-${s.year}`])}
                      onChange={(e) => {
                        if (e.target.checked) {
                          const all = {};
                          suggestions.forEach((s) => { all[`${s.property_id}-${s.year}`] = true; });
                          setSelectedSuggestions(all);
                        } else setSelectedSuggestions({});
                      }}
                      data-testid="assistant-select-all"
                    />
                  </th>
                  <th className="px-3 py-2 text-left">Objekt</th>
                  <th className="px-3 py-2 text-left">Wirtschaftsjahr</th>
                  <th className="px-3 py-2 text-left">Zeitraum</th>
                  <th className="px-3 py-2 text-left">Hinweis</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {suggestions.map((s) => {
                  const key = `${s.property_id}-${s.year}`;
                  return (
                    <tr key={key} className="hover:bg-slate-50">
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={!!selectedSuggestions[key]}
                          onChange={(e) => setSelectedSuggestions((cur) => ({ ...cur, [key]: e.target.checked }))}
                          data-testid={`assistant-check-${key}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-900">{s.property_name}</div>
                        {s.property_number && (
                          <div className="text-[10px] text-slate-500">{s.property_number}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 tabular-nums font-mono">{s.year}</td>
                      <td className="px-3 py-2 text-xs text-slate-600 tabular-nums">
                        {formatDate(s.period_from)} – {formatDate(s.period_to)}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500">{s.reason}</td>
                    </tr>
                  );
                })}
                {suggestions.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-3 py-8 text-center text-slate-400 text-sm">
                      Keine offenen Vorschläge – alle abrechnungsreifen Jahre wurden bereits angelegt.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssistantOpen(false)}>Abbrechen</Button>
            <Button onClick={handleCreateSuggestions} disabled={creatingSuggestions} data-testid="assistant-create-btn">
              {creatingSuggestions ? "Lege an…" : "Ausgewählte anlegen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
function NewPeriodDialog({ properties, onCreated }) {
  const [propertyId, setPropertyId] = useState("");
  const [year, setYear] = useState(String(new Date().getFullYear() - 1));
  const [mode, setMode] = useState("objekt");
  const [label, setLabel] = useState("");
  const [segUnitIds, setSegUnitIds] = useState([]);
  const [segAllUnits, setSegAllUnits] = useState(true);
  const [units, setUnits] = useState([]);
  const [reference, setReference] = useState("");
  const [saving, setSaving] = useState(false);

  const selectedProp = properties.find((p) => p.id === propertyId);
  const isWegExternal = selectedProp?.weg_management === "external" || selectedProp?.object_type === "seg";

  useEffect(() => {
    if (isWegExternal && mode !== "seg_import") setMode("seg_import");
    if (!isWegExternal && mode === "seg_import") setMode("objekt");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  useEffect(() => {
    if (!propertyId) { setUnits([]); return; }
    api.get(`/properties/${propertyId}`)
      .then(({ data }) => {
        setUnits(data?.units || []);
        setSegUnitIds([]);
        setSegAllUnits(true);
      })
      .catch(() => setUnits([]));
  }, [propertyId]);

  const toggleSegUnit = (uid) => {
    setSegUnitIds((prev) => prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]);
  };

  const submit = async () => {
    if (!propertyId || !year) return;
    setSaving(true);
    try {
      // Bei SEG-Import: entweder ALLE Einheiten (segAllUnits) oder Auswahl
      const effectiveSegIds = mode === "seg_import"
        ? (segAllUnits ? units.map((u) => u.id) : segUnitIds)
        : [];
      const payload = {
        property_id: propertyId,
        period_from: `${year}-01-01`,
        period_to: `${year}-12-31`,
        label: label || `Wirtschaftsjahr ${year}`,
        mode,
        seg_unit_ids: effectiveSegIds,
        external_weg_reference: reference || null,
        // 2026-02: bei SEG-Sammellauf verteilen wir standardmäßig nach MEA
        default_key: mode === "seg_import" ? "mea" : "living_area",
      };
      await api.post("/bka/periods", payload);
      toast.success(`Abrechnung angelegt${mode === "seg_import" && effectiveSegIds.length > 1 ? ` — Sammellauf für ${effectiveSegIds.length} Einheiten` : ""}`);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent className="max-w-lg" data-testid="bka-new-dialog">
      <DialogHeader>
        <DialogTitle>Neue Betriebskostenabrechnung</DialogTitle>
        <DialogDescription>
          Wähle Objekt und Wirtschaftsjahr. Bei einer Einheit in einer fremd-verwalteten WEG
          wählst du „SEG-Import" — hier übernimmst du die Werte manuell aus der WEG-Jahresabrechnung.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <Label>Objekt</Label>
          <SearchableCombobox
            value={propertyId} onChange={setPropertyId} testId="bka-new-property"
            placeholder="Objekt suchen (Name, Objekt-ID, Adresse)…"
            options={sortByPropertyNumber(properties).map((p) => ({
              id: p.id,
              label: `${p.property_number ? `${p.property_number} · ` : ""}${p.name}`,
              sublabel: [addressText(p), p.object_type?.toUpperCase()].filter(Boolean).join(" · "),
              keywords: [p.property_number, p.name, addressText(p)].filter(Boolean).join(" "),
            }))}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Wirtschaftsjahr</Label>
            <Input type="number" min="2000" max="2099" value={year}
                   onChange={(e) => setYear(e.target.value)} data-testid="bka-new-year" />
          </div>
          <div>
            <Label>Bezeichnung (optional)</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)}
                   placeholder={`Wirtschaftsjahr ${year}`} data-testid="bka-new-label" />
          </div>
        </div>
        <div>
          <Label>Modus</Label>
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger data-testid="bka-new-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="objekt">Objektabrechnung (normal)</SelectItem>
              <SelectItem value="seg_import">SEG-Import (aus fremder WEG-Abrechnung)</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-[11px] text-slate-500 mt-1">
            {mode === "objekt"
              ? "Alle Kosten des Objekts selbst erfassen (Mietshaus/EFH oder WEG in Eigenverwaltung)."
              : "Werte aus fremder WEG-Jahresabrechnung anteilig übernehmen (typisch: 1 Einheit in großer WEG)."}
          </p>
        </div>
        {mode === "seg_import" && (
          <>
            <div className="border border-slate-200 rounded p-3 bg-slate-50/70">
              <div className="flex items-start gap-2 mb-2">
                <input type="checkbox" id="seg-all" className="mt-0.5"
                       checked={segAllUnits} onChange={(e) => setSegAllUnits(e.target.checked)}
                       data-testid="bka-seg-all-units" />
                <label htmlFor="seg-all" className="text-sm text-slate-800 cursor-pointer">
                  <b>Alle Einheiten dieser WEG in einem Lauf abrechnen</b>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    Empfohlen — das System erzeugt automatisch eine Abrechnung pro Einheit
                    und verteilt die WEG-Kosten nach MEA (Miteigentumsanteil).
                  </div>
                </label>
              </div>
              {!segAllUnits && (
                <div className="mt-3">
                  <Label className="text-xs">Abzurechnende Einheiten (Mehrfachauswahl)</Label>
                  <div className="mt-1 max-h-40 overflow-y-auto border border-slate-200 rounded bg-white">
                    {units.map((u) => (
                      <label key={u.id}
                             className="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-b-0">
                        <input type="checkbox" checked={segUnitIds.includes(u.id)}
                               onChange={() => toggleSegUnit(u.id)}
                               data-testid={`bka-seg-unit-${u.id}`} />
                        <span className="flex-1">
                          <strong>{u.unit_number}</strong>
                          {u.living_area ? ` · ${u.living_area} m²` : ""}
                          {u.weg_share ? ` · MEA ${u.weg_share}` : ""}
                        </span>
                      </label>
                    ))}
                    {units.length === 0 && (
                      <div className="text-xs text-slate-500 p-3 text-center">Keine Einheiten vorhanden.</div>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">{segUnitIds.length} von {units.length} ausgewählt</p>
                </div>
              )}
            </div>
            <div>
              <Label>Referenz fremde WEG-Abrechnung (optional)</Label>
              <Textarea rows={2} value={reference} onChange={(e) => setReference(e.target.value)}
                        placeholder="z. B. Hausverwaltung Schmidt, Jahresabrechnung 2024 vom 01.03.2025"
                        data-testid="bka-new-ref" />
            </div>
          </>
        )}
      </div>
      <DialogFooter>
        <Button onClick={submit}
                disabled={!propertyId || saving || (mode === "seg_import" && !segAllUnits && segUnitIds.length === 0)}
                data-testid="bka-new-submit">
          {saving ? "Speichere…" : "Anlegen"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
